import * as Sentry from "@sentry/react";
import type { ErrorInfo, ReactNode } from "react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { READ_ONLY_REFUSAL } from "@yagni/shared";
import { Button, Card } from "./ui/index.js";

let initialized = false;

const SECRET_KEY_PATTERNS = [
  /authorization/i,
  /cookie/i,
  /password/i,
  /^(access|refresh|id|session|csrf|auth|bearer|jwt)[_-]?token$/i,
  /(api|secret|private|auth|encryption)[_-]?key/i,
  /^token$/i,
  /^secret$/i,
  /^bearer$/i,
  /credentials/i,
  /client[_-]?secret/i,
  /stripe[_-]?(secret|signing|signature)/i,
];

const MAX_SCRUB_DEPTH = 20;

function keyLooksSensitive(key: string): boolean {
  return SECRET_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

export function scrubPII<T>(value: T, depth = 0): T {
  if (depth > MAX_SCRUB_DEPTH || value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => scrubPII(item, depth + 1)) as unknown as T;
  }
  if (typeof value !== "object") return value;
  const source = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(source)) {
    if (keyLooksSensitive(key)) {
      out[key] = "[Filtered]";
      continue;
    }
    out[key] = scrubPII(val, depth + 1);
  }
  return out as unknown as T;
}

function resolveSampleRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback;
  return parsed;
}

export function initSentry(): boolean {
  if (initialized) return true;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return false;

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    import.meta.env.MODE;
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
  const isProd = environment === "production";

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    tracesSampleRate: resolveSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
      isProd ? 0.1 : 1.0,
    ),
    replaysSessionSampleRate: resolveSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined,
      isProd ? 0.0 : 0.1,
    ),
    replaysOnErrorSampleRate: resolveSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined,
      1.0,
    ),
    integrations: [
      // react-router v7 integration captures navigation transactions
      // (route changes) in addition to initial page loads. Using the
      // generic browserTracingIntegration here would miss SPA route
      // changes.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
        // Stripe Elements mounts cross-origin iframes (js.stripe.com,
        // m.stripe.network). rrweb walks the DOM trying to read iframe
        // contents for replay, the browser blocks cross-origin access,
        // and emits a "Blocked a frame with origin ..." console error.
        // Blocking the iframes here keeps rrweb from descending into
        // them — replay can't record their contents anyway.
        block: [
          'iframe[src*="js.stripe.com"]',
          'iframe[src*="stripe.network"]',
          'iframe[src*="m.stripe.com"]',
          'iframe[name^="__privateStripe"]',
        ],
      }),
    ],
    beforeSend(event) {
      if (event.request?.headers) {
        event.request.headers = scrubPII(event.request.headers);
      }
      if (event.request?.cookies) {
        event.request.cookies = scrubPII(event.request.cookies);
      }
      if (event.extra) event.extra = scrubPII(event.extra);
      if (event.contexts) event.contexts = scrubPII(event.contexts);
      if (event.tags) event.tags = scrubPII(event.tags);
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((crumb) => ({
          ...crumb,
          data: crumb.data ? scrubPII(crumb.data) : crumb.data,
        }));
      }
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((ex) => {
          if (!ex.mechanism?.data) return ex;
          return {
            ...ex,
            mechanism: { ...ex.mechanism, data: scrubPII(ex.mechanism.data) },
          };
        });
      }
      return event;
    },
  });

  Sentry.setTag("component", "frontend");
  initialized = true;
  return true;
}

export function isSentryInitialized(): boolean {
  return initialized;
}

export function resetSentryForTests(): void {
  initialized = false;
}

export interface UiErrorMetadata {
  area: string;
  operation: string;
  route?: string | null;
  workspaceId?: string | null;
  severity?: "fatal" | "error" | "warning";
  expected?: boolean;
  [key: string]: unknown;
}

interface CaptureUiErrorOptions {
  captureException?: typeof Sentry.captureException;
}

const UI_TAG_KEYS = new Set([
  "area",
  "operation",
  "route",
  "workspaceId",
  "severity",
  "expected",
]);

function uiMetadataExtra(metadata: UiErrorMetadata): Record<string, unknown> {
  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (UI_TAG_KEYS.has(key)) continue;
    extra[key] = value;
  }
  return scrubPII(extra);
}

/**
 * The sentence the backend's read-only guard answers a write with (403),
 * shared with the backend so the two cannot drift: a super admin viewing a
 * workspace in Observe pressed a verb. The person reads it beside the verb.
 */
export { READ_ONLY_REFUSAL };

/**
 * Whether a failure is the read-only guard working. Every act helper throws
 * the route's own sentence (`usePageActs`, `useSheetActs`, `GateResolveError`),
 * so the sentence is what identifies it.
 */
export function isReadOnlyRefusal(err: unknown): boolean {
  return err instanceof Error && err.message.trim() === READ_ONLY_REFUSAL;
}

/**
 * Report a UI failure to Sentry. The read-only refusal is not one: it is the
 * guard doing its job and is already shown to the person, so it is never sent
 * (YAGNI-FRONTEND-45 / -4E were Observe-mode Stop and Run again presses).
 * Returns whether an event was captured.
 */
export function captureUiError(
  err: unknown,
  metadata: UiErrorMetadata,
  options: CaptureUiErrorOptions = {},
): boolean {
  if (isReadOnlyRefusal(err)) return false;
  const severity = metadata.severity ?? "error";
  const tags: Record<string, string> = {
    area: metadata.area,
    operation: metadata.operation,
    severity,
    expected: metadata.expected ? "true" : "false",
  };
  if (metadata.route) tags.route = metadata.route;
  if (metadata.workspaceId) tags.workspace_id = metadata.workspaceId;

  try {
    (options.captureException ?? Sentry.captureException)(err, {
      level: severity,
      tags,
      extra: uiMetadataExtra(metadata),
    });
    return true;
  } catch {
    return false;
  }
}

type ReactRootErrorHandler = (
  callback?: (error: unknown, errorInfo: ErrorInfo) => void,
) => (error: unknown, errorInfo: ErrorInfo) => void;

interface SentryReactRootOptionsDeps {
  reactErrorHandler?: ReactRootErrorHandler;
}

export interface SentryReactRootOptions {
  onUncaughtError?: (error: unknown, errorInfo: ErrorInfo) => void;
  onCaughtError?: (error: unknown, errorInfo: ErrorInfo) => void;
  onRecoverableError?: (error: unknown, errorInfo: ErrorInfo) => void;
}

export function getSentryReactRootOptions(
  deps: SentryReactRootOptionsDeps = {},
): SentryReactRootOptions {
  const reactErrorHandler = deps.reactErrorHandler ?? Sentry.reactErrorHandler;
  return {
    onUncaughtError: reactErrorHandler(() => {}),
    onCaughtError: reactErrorHandler(),
    onRecoverableError: reactErrorHandler(),
  };
}

interface AppErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

function AppErrorFallback({ error, resetError }: AppErrorFallbackProps): ReactNode {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="min-h-screen w-full flex items-center justify-center [background:var(--canvas-field)] p-lg">
      <Card tone="error" padding="lg" className="max-w-md w-full flex flex-col gap-md">
        <h1 className="text-lg font-semibold font-display text-text-primary">
          Something went wrong.
        </h1>
        <p className="text-sm text-text-secondary">
          YAGNI hit an unexpected error and has reported it to the team. You can try
          recovering this view, or reload the page if the issue persists.
        </p>
        <pre className="text-xs text-text-tertiary bg-[var(--bg-code)] font-mono rounded-md p-sm whitespace-pre-wrap break-words max-h-32 overflow-auto">
          {message}
        </pre>
        <div className="flex gap-sm">
          <Button onClick={resetError} variant="primary">Try again</Button>
          <Button onClick={() => window.location.reload()} variant="secondary">
            Reload page
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function SentryErrorBoundary({ children }: { children: ReactNode }): ReactNode {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <AppErrorFallback error={error} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
